import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/i18n";
import { MessageCircle, X, Send, Loader2, Bot } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sid = () => {
  let s = localStorage.getItem("cr_chat_sid");
  if (!s) {
    s = crypto.randomUUID();
    localStorage.setItem("cr_chat_sid", s);
  }
  return s;
};

export default function ChatWidget() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", text: t("chat.greeting") }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid(), message: msg }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const d = JSON.parse(payload);
            if (d.t) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + d.t };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: t("chat.error") };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const chips = t("chat.chips").split("|");

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed z-[60] bottom-36 lg:bottom-24 right-4 lg:right-6 w-[calc(100vw-2rem)] max-w-sm bg-white border border-[#E2E8F0] rounded-3xl shadow-2xl overflow-hidden flex flex-col"
            style={{ height: "min(560px, calc(100vh - 12rem))" }}
            data-testid="chat-panel"
          >
            <div className="flex items-center gap-3 px-5 py-4 bg-[#003087]">
              <span className="w-9 h-9 rounded-full bg-[#00AEEF]/20 border border-[#00AEEF]/40 flex items-center justify-center">
                <Bot size={18} className="text-[#9BDDF7]" />
              </span>
              <div className="flex-1">
                <p className="text-sm font-bold text-white leading-tight">{t("chat.title")}</p>
                <p className="text-[11px] text-[#9BDDF7] flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#00AEEF] inline-block" /> {t("chat.sub")}
                </p>
              </div>
              <button data-testid="chat-close-btn" onClick={() => setOpen(false)} className="text-white/60 hover:text-white transition-colors duration-200 p-1" aria-label="chiudi chat">
                <X size={18} />
              </button>
            </div>

            <div ref={boxRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FFFFFF]" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${i}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#00AEEF] text-white font-medium rounded-br-md"
                        : "bg-white text-[#003087] border border-[#E2E8F0] rounded-bl-md shadow-sm"
                    }`}
                  >
                    {m.text || (busy && i === messages.length - 1 ? <Loader2 size={15} className="animate-spin text-[#00AEEF]" /> : "")}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-3 flex flex-wrap gap-2 bg-[#FFFFFF]" data-testid="chat-chips">
                {chips.map((c, i) => (
                  <button key={i} data-testid={`chat-chip-${i}`} onClick={() => send(c)}
                    className="text-[11px] font-semibold border border-[#00AEEF]/40 text-[#00AEEF] bg-white rounded-full px-3 py-1.5 hover:bg-[#00AEEF]/10 transition-colors duration-200">
                    {c}
                  </button>
                ))}
              </div>
            )}

            <div className="p-3 border-t border-[#E2E8F0] bg-white">
              <div className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t("chat.placeholder")}
                  className="input-field !rounded-full"
                />
                <button data-testid="chat-send-btn" onClick={() => send()} disabled={busy || !input.trim()}
                  className="btn-green w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-50 shrink-0" aria-label="invia">
                  <Send size={16} />
                </button>
              </div>
              <p className="text-center text-[10px] text-[#94A3B8] mt-2">{t("chat.disclaimer")}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="live-chat-toggle-button"
        onClick={() => setOpen(!open)}
        className="pulse-ring fixed z-[60] bottom-[5.5rem] lg:bottom-6 right-4 lg:right-6 w-14 h-14 rounded-full bg-[#00AEEF] text-white flex items-center justify-center shadow-2xl hover:scale-105 transition-transform duration-200"
        aria-label="apri chat assistente"
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
      </button>
    </>
  );
}
